/**
 * Camera self-fade: heroes the camera is about to clip (the followed hero when zoomed right in,
 * party members standing between the camera and its pivot) fade to partial opacity and back.
 *
 * Materials can be shared between unit views (cached toon materials, cloned VRMs), so a fading
 * unit swaps its meshes onto per-unit transparent clones (made once, on its first fade) and back
 * to the originals when it's opaque again; its outline hulls hide while faded. The fade amount
 * math is pure (`fadeTarget`, unit-tested).
 */
import * as THREE from 'three';
import type { UnitId } from '@keystone/sim';

/** Fade rate toward the target (1/s). */
const FADE_RATE = 10;
/** Lowest opacity of a faded hero. */
export const FADE_MIN = 0.22;
/** Party members blocking the view fade to this. */
export const FADE_BLOCK = 0.4;
const NEAR_FULL = 2;
const NEAR_MIN = 0.5;
const CAPSULE_R = 0.4;

const smooth = (t: number): number => {
  const x = Math.min(1, Math.max(0, t));
  return x * x * (3 - 2 * x);
};

/**
 * Target opacity from the eye's distance to the hero's body (a vertical capsule) and, for party
 * members, whether they stand between the eye and the pivot. All in yards.
 */
export function fadeTarget(eyeToBody: number, blocking: boolean): number {
  const near = FADE_MIN + (1 - FADE_MIN) * smooth((eyeToBody - NEAR_MIN) / (NEAR_FULL - NEAR_MIN));
  return blocking ? Math.min(near, FADE_BLOCK) : near;
}

/** Distance from point p to the vertical capsule at (cx, cz), y in [y0, y1] (three.js space). */
export function capsuleDistance(px: number, py: number, pz: number, cx: number, cz: number, y0: number, y1: number, r = CAPSULE_R): number {
  const y = Math.min(y1, Math.max(y0, py));
  return Math.max(0, Math.hypot(px - cx, py - y, pz - cz) - r);
}

/**
 * Whether point c sits on the segment eye→pivot (within `r`) and nearer the eye than the pivot
 * (three.js space). `along` must leave the pivot end out so the followed hero never counts.
 */
export function blocksView(ex: number, ey: number, ez: number, px: number, py: number, pz: number, cx: number, cy: number, cz: number, r = 0.55): boolean {
  const dx = px - ex;
  const dy = py - ey;
  const dz = pz - ez;
  const len2 = dx * dx + dy * dy + dz * dz;
  if (len2 < 1e-6) return false;
  const t = ((cx - ex) * dx + (cy - ey) * dy + (cz - ez) * dz) / len2;
  if (t <= 0 || t >= 0.85) return false;
  return Math.hypot(ex + dx * t - cx, ey + dy * t - cy, ez + dz * t - cz) < r;
}

interface Entry {
  alpha: number;
  target: number;
  meshes: THREE.Mesh[];
  orig: (THREE.Material | THREE.Material[])[];
  faded: (THREE.Material | THREE.Material[])[] | null;
  hulls: THREE.Object3D[];
  swapped: boolean;
}

export class SelfFade {
  private readonly entries = new Map<UnitId, Entry>();
  outlines = true;

  /** Request a target opacity this frame (units not requested return to opaque). */
  want(id: UnitId, root: THREE.Object3D, hitbox: THREE.Object3D, target: number): void {
    let e = this.entries.get(id);
    if (!e) {
      if (target >= 0.999) return;
      e = collect(root, hitbox);
      this.entries.set(id, e);
    }
    e.target = Math.min(e.target, target);
  }

  /** Ease every fading unit; call once per frame after the `want` calls. */
  update(dtS: number): void {
    const k = 1 - Math.exp(-FADE_RATE * dtS);
    for (const [id, e] of this.entries) {
      e.alpha += (e.target - e.alpha) * k;
      if (Math.abs(e.target - e.alpha) < 0.005) e.alpha = e.target;
      e.target = 1;
      if (e.alpha >= 0.995) {
        this.restore(e);
        disposeFaded(e);
        this.entries.delete(id);
        continue;
      }
      this.apply(e);
    }
  }

  /** A unit view went away (its materials are disposed with it; drop the clones). */
  forget(id: UnitId): void {
    const e = this.entries.get(id);
    if (!e) return;
    this.restore(e);
    disposeFaded(e);
    this.entries.delete(id);
  }

  private apply(e: Entry): void {
    if (!e.faded) e.faded = e.orig.map((m) => (Array.isArray(m) ? m.map(fadedClone) : fadedClone(m)));
    if (!e.swapped) {
      e.meshes.forEach((m, i) => (m.material = e.faded![i]!));
      e.swapped = true;
    }
    for (const f of e.faded) for (const m of Array.isArray(f) ? f : [f]) setOpacity(m, e.alpha * ((m.userData['baseOpacity'] as number | undefined) ?? 1));
    const showHull = this.outlines && e.alpha > 0.9;
    for (const h of e.hulls) h.visible = showHull;
  }

  private restore(e: Entry): void {
    if (e.swapped) {
      e.meshes.forEach((m, i) => (m.material = e.orig[i]!));
      e.swapped = false;
    }
    for (const h of e.hulls) h.visible = this.outlines;
  }

  dispose(): void {
    for (const e of this.entries.values()) {
      this.restore(e);
      disposeFaded(e);
    }
    this.entries.clear();
  }
}

function collect(root: THREE.Object3D, hitbox: THREE.Object3D): Entry {
  const meshes: THREE.Mesh[] = [];
  const hulls: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (o === hitbox) return;
    if (o.userData['hull']) {
      hulls.push(o);
      return;
    }
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.material || !(m.layers.mask & 1)) return;
    // Additive fx meshes (glows, trails) keep their own blending.
    const mats = Array.isArray(m.material) ? m.material : [m.material];
    if (mats.some((x) => x.blending !== THREE.NormalBlending)) return;
    meshes.push(m);
  });
  return { alpha: 1, target: 1, meshes, orig: meshes.map((m) => m.material), faded: null, hulls, swapped: false };
}

function fadedClone(m: THREE.Material): THREE.Material {
  const c = m.clone();
  c.userData = { ...m.userData, baseOpacity: m.opacity ?? 1, baseAlphaTest: m.alphaTest ?? 0 };
  // Opaque / cutout surfaces keep writing depth (no see-through self-overlap); already-blended
  // ones (hair, lashes) keep their own setting.
  if (!m.transparent) c.depthWrite = true;
  c.transparent = true;
  return c;
}

function setOpacity(m: THREE.Material, a: number): void {
  m.opacity = a;
  // ShaderMaterials (MToon) read `opacity` / `alphaTest` uniforms.
  const uni = (m as THREE.ShaderMaterial).uniforms;
  if (uni?.['opacity']) uni['opacity'].value = a;
  // Cutout surfaces (alphaTest 0.5) would discard everything once opacity drops under the
  // threshold: scale the threshold with the fade (kept > 0 so the shader variant stays the same).
  const base = (m.userData['baseAlphaTest'] as number | undefined) ?? 0;
  if (base > 0) {
    const at = Math.max(0.01, base * a);
    m.alphaTest = at;
    if (uni?.['alphaTest']) uni['alphaTest'].value = at;
  }
}

function disposeFaded(e: Entry): void {
  if (!e.faded) return;
  for (const f of e.faded) for (const m of Array.isArray(f) ? f : [f]) m.dispose();
  e.faded = null;
}
